import { useAIEdit } from 'hooks/useAIEdit'
import moment from 'moment'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { EuiButton, EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiPanel, EuiSpacer, EuiText, EuiTitle, EuiToolTip } from '@elastic/eui'

import { IntervalUnit } from 'api/interfaces'
import { AIEditFlyoutState, AIEditTargetType } from 'api/interfaces/AIEditTypes'
import { AIPlan, AIPlanCampaignBuilder, AIPlanCampaignBuilderType, AIPlanStatus, AIPlanStub } from 'api/interfaces/AIPlan'
import { MasterCampaignPlanCampaignBuilder } from 'api/interfaces/MasterCampaignStub'
import {
  useGetAIPlanMediaPlanSectionQuery,
  useUpdateAIPlanMediaPlanCampaignDetailsTitleMutation,
  useHideAIPlanMediaPlanSectionMutation,
  useAddAIPlanMediaPlanCustomSectionMutation,
  useUpdateAIPlanMediaPlanCustomSectionMutation,
  useDeleteAIPlanMediaPlanCustomSectionMutation,
  useUpdateAIPlanMediaPlanPageTitleMutation,
  useRemoveAIPlanStubCampaignBuilderMutation,
  useCopyAIPlanToDspMutation,
  useCopyAIPlanCampaignToDspMutation,
  useEditAIPlanStubCampaignBuilderMutation,
  useAiEditAIPlanMutation
} from 'api/rtkQueryApi/platform/aiPlansApi'
import { RootState } from 'app/rootReducer'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import AIEditPromptFlyout from 'features/planner/components/AIEditPromptFlyout'
import AgencyV2Modal from 'features/planner/components/AgencyV2Modal'
import GanttChart from 'features/planner/components/GanttChart/GanttChart'
import SparklesIcon from 'features/planner/components/SparklesIcon'
import BackgroundContentCard from 'features/planner/pages/MasterCampaignPlanPage/components/BackgroundContentCard'
import { AddCustomSectionButton } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/AddCustomSectionButton'
import EditCustomSectionFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditCustomSectionFlyout'
import EditPageTitleFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditPageTitleFlyout'
import { CampaignSpecificBillboardMap, CampaignSpecificBillboardMapRef } from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/CampaignSpecificMaps'
import { RenderCustomSections } from 'features/planner/pages/MasterCampaignPlanPage/components/shared/RenderCustomSections'
import { downloadPdf } from 'utils/pdfDownloader'

import { selectAIPlanEditMode } from '../../aiPlanSlice'
import { sortAIPlanCampaigns } from '../../utils/aiPlanContentUtils'
import AIPlanMediaPlanPdf from '../AIPlanMediaPlanPdf'
import AIPlanTargetedHouseholdLocations, { AIPlanTargetedHouseholdLocationsRef } from '../Projections/AIPlanTargetedHouseholdLocations'
import AIPlanBudget from './AIPlanBudget'
import AIPlanCampaignCard from './AIPlanCampaignCard'
import AddAIPlanCampaignFlyout from './AddAIPlanCampaignFlyout'
import EditAIPlanCampaignAudienceFlyout from './EditAIPlanCampaignAudienceFlyout'
import EditAIPlanCampaignBasicsFlyout from './EditAIPlanCampaignBasicsFlyout'
import EditAIPlanCampaignFlyout, { EditAIPlanCampaignFlyoutMode } from './EditAIPlanCampaignFlyout'
import EditAIPlanCampaignKeywordsFlyout from './EditAIPlanCampaignKeywordsFlyout'
import EditAIPlanCampaignProjectionsFlyout from './EditAIPlanCampaignProjectionsFlyout'
import EditAIPlanCampaignSectionFlyout, { CampaignSectionType } from './EditAIPlanCampaignSectionFlyout'

type SectionType = 'budget'
type MediaPlanCustomSectionPosition = 'aboveCampaignTimeline' | 'aboveCampaignDetails'

export interface AIPlanMediaPlanRef {
  getMapImages: () => Promise<Record<string, string | null>>
}

export interface AIPlanMediaPlanProps {
  aiPlan: AIPlan
  stub?: AIPlanStub
  isStubLoading: boolean
  onAllStaffPdfsClick?: () => void
  staffPdfDisabledReason?: string
}

interface EditCampaignFlyoutState {
  mode: EditAIPlanCampaignFlyoutMode
  selectedCampaignBuilderId?: string
  offAdCritterName?: string
  offAdCritterColor?: string
}

interface EditSectionFlyoutState {
  campaignBuilderId: string
  sectionType: CampaignSectionType
}

interface EditCampaignNameState {
  campaignBuilderId: string
  currentName: string
}

const AIPlanMediaPlan = forwardRef<AIPlanMediaPlanRef, AIPlanMediaPlanProps>(({ aiPlan, stub, isStubLoading, onAllStaffPdfsClick, staffPdfDisabledReason }, ref) => {
  const history = useHistory()
  const editMode = useSelector(selectAIPlanEditMode)
  const { currentOrganization, currentAccount } = useSelector((state: RootState) => state.app)

  const [editCampaignFlyoutOpen, setEditCampaignFlyoutOpen] = useState<EditCampaignFlyoutState | null>(null)
  const [editBasicsFlyoutCampaignId, setEditBasicsFlyoutCampaignId] = useState<string | null>(null)
  const [editSectionFlyoutState, setEditSectionFlyoutState] = useState<EditSectionFlyoutState | null>(null)
  const [editCampaignNameState, setEditCampaignNameState] = useState<EditCampaignNameState | null>(null)
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [deletingSectionType, setDeletingSectionType] = useState<SectionType | null>(null)
  const [addingCustomSectionPosition, setAddingCustomSectionPosition] = useState<MediaPlanCustomSectionPosition | null>(null)
  const [editingCustomSection, setEditingCustomSection] = useState<{ position: MediaPlanCustomSectionPosition; index: number } | null>(null)
  const [deletingCustomSection, setDeletingCustomSection] = useState<{ position: MediaPlanCustomSectionPosition; index: number } | null>(null)
  const [editingPageTitle, setEditingPageTitle] = useState(false)
  const [editingCampaignDetailsTitle, setEditingCampaignDetailsTitle] = useState(false)
  const [aiEditFlyoutState, setAiEditFlyoutState] = useState<AIEditFlyoutState | null>(null)
  const [isAddCampaignFlyoutOpen, setIsAddCampaignFlyoutOpen] = useState(false)
  const [projectionSettingsCampaignId, setProjectionSettingsCampaignId] = useState<string | null>(null)

  const didFireNameUpdate = useRef(false)
  const previousCampaignIdsRef = useRef<Set<string>>(new Set())
  const campaignMapRefs = useRef<Map<string, AIPlanTargetedHouseholdLocationsRef | CampaignSpecificBillboardMapRef>>(new Map())

  const captureMapImages = async (): Promise<Record<string, string | null>> => {
    const mapImages: Record<string, string | null> = {}
    const campaignsWithZips = (stub?.campaignBuilders ?? []).filter(c => c.zips && c.zips.length > 0)

    const waitForMapReady = (checkIsReady: () => boolean, maxAttempts = 40, interval = 250): Promise<void> => {
      return new Promise(resolve => {
        let attempts = 0
        const check = () => {
          if (checkIsReady()) {
            resolve()
          } else if (attempts >= maxAttempts) {
            resolve()
          } else {
            attempts++
            setTimeout(check, interval)
          }
        }
        check()
      })
    }

    const readinessPromises = campaignsWithZips.map(async campaign => {
      const maxAttempts = 5
      let attempts = 0
      while (attempts < maxAttempts) {
        attempts++
        const mapRef = campaignMapRefs.current.get(campaign.id)
        if (mapRef) {
          return waitForMapReady(() => mapRef.isMapReady())
        }
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    })

    await Promise.all(readinessPromises)
    await new Promise(resolve => setTimeout(resolve, 500))

    for (const campaign of campaignsWithZips) {
      const mapRef = campaignMapRefs.current.get(campaign.id)
      if (mapRef) {
        try {
          mapImages[campaign.id] = await mapRef.getMapCanvas()
        } catch {
          mapImages[campaign.id] = null
        }
      }
    }

    return mapImages
  }

  useImperativeHandle(ref, () => ({
    getMapImages: captureMapImages
  }))

  const { data: mediaPlanSection } = useGetAIPlanMediaPlanSectionQuery({ aiPlanId: aiPlan.id })

  const [updateCampaignDetailsTitle, { isLoading: isUpdatingCampaignDetailsTitle }] = useUpdateAIPlanMediaPlanCampaignDetailsTitleMutation()
  const [hideSection, { isLoading: isHidingSection }] = useHideAIPlanMediaPlanSectionMutation()
  const [addCustomSection, { isLoading: isAddingCustomSection }] = useAddAIPlanMediaPlanCustomSectionMutation()
  const [updateCustomSection, { isLoading: isUpdatingCustomSection }] = useUpdateAIPlanMediaPlanCustomSectionMutation()
  const [deleteCustomSection, { isLoading: isDeletingCustomSection }] = useDeleteAIPlanMediaPlanCustomSectionMutation()
  const [updatePageTitle, { isLoading: isUpdatingPageTitle }] = useUpdateAIPlanMediaPlanPageTitleMutation()
  const [removeCampaignBuilder, { isLoading: isRemovingCampaign }] = useRemoveAIPlanStubCampaignBuilderMutation()
  const [copyToDsp, { isLoading: isCopying, isSuccess: isCopySuccess, data: copyResult }] = useCopyAIPlanToDspMutation()
  const [copyCampaignToDsp, { isLoading: isCopyingCampaign }] = useCopyAIPlanCampaignToDspMutation()
  const [editCampaignBuilder, { isSuccess: isEditCampaignNameSuccess, isLoading: isEditingCampaignName }] = useEditAIPlanStubCampaignBuilderMutation()
  const [aiEditAIPlan] = useAiEditAIPlanMutation()

  const { submit: submitAIEdit, isSubmitting: isAIEditSubmitting } = useAIEdit({
    mutationFn: aiEditAIPlan,
    entityId: aiPlan.id,
    onSuccess: () => {
      setAiEditFlyoutState(null)
      setIsAddCampaignFlyoutOpen(false)
    }
  })

  const campaignSectionToTargetType: Record<string, AIEditTargetType> = {
    summary: 'CampaignSummary',
    timing: 'CampaignTiming',
    geography: 'CampaignGeography',
    audienceSegments: 'CampaignSegments',
    audience: 'CampaignAudience',
    keywords: 'CampaignKeywords'
  }

  useEffect(() => {
    if (isCopySuccess && copyResult) {
      setIsCopyModalVisible(false)
    }
  }, [isCopySuccess, copyResult])

  useEffect(() => {
    if (didFireNameUpdate.current && isEditCampaignNameSuccess) {
      didFireNameUpdate.current = false
      setEditCampaignNameState(null)
    }
  }, [isEditCampaignNameSuccess])

  useEffect(() => {
    const currentIds = new Set(stub?.campaignBuilders?.map(c => c.id) ?? [])
    const previousIds = previousCampaignIdsRef.current

    if (previousIds.size > 0) {
      const newId = [...currentIds].find(id => !previousIds.has(id))
      if (newId) {
        setTimeout(() => {
          const element = document.getElementById(`campaign-${newId}`)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
      }
    }

    previousCampaignIdsRef.current = currentIds
  }, [stub?.campaignBuilders])

  const campaigns = sortAIPlanCampaigns(stub?.campaignBuilders ?? [])
  const planStartDate = moment(aiPlan.aiUsedStartDate || aiPlan.planStartDate)
  const planDurationMonths = aiPlan.aiUsedDurationInMonths || aiPlan.planDurationInMonths
  const showActions = editMode && aiPlan.status !== AIPlanStatus.CopiedToDsp

  const isUpdatingAnySection = isAddingCustomSection || isUpdatingCustomSection

  const budgetHidden = mediaPlanSection?.budget?.hideAll === true

  const onAddCampaignClick = () => {
    setIsAddCampaignFlyoutOpen(true)
  }

  const onRemoveCampaignClick = async (builder: AIPlanCampaignBuilder) => {
    if (!stub) return
    await removeCampaignBuilder({
      aiPlanId: aiPlan.id,
      aiPlanStubId: stub.id,
      campaignBuilderId: builder.id
    })
  }

  const onCopyCampaignToDsp = async (builder: AIPlanCampaignBuilder) => {
    if (!currentOrganization || !currentAccount) return
    await copyCampaignToDsp({
      organizationId: currentOrganization.id,
      accountId: currentAccount.id,
      aiPlanId: aiPlan.id,
      campaignBuilderId: builder.id
    })
  }

  const onCopyToDsp = async () => {
    if (!currentOrganization || !currentAccount) return
    await copyToDsp({
      organizationId: currentOrganization.id,
      accountId: currentAccount.id,
      aiPlanId: aiPlan.id
    })
  }

  const onViewInDspClick = (dspCampaignId: string) => {
    history.push(`/campaigns/edit/${dspCampaignId}/details`)
  }

  const handleSaveCampaignName = (campaignBuilderId: string, newName: string) => {
    if (!stub) return

    const builder = stub.campaignBuilders.find(b => b.id === campaignBuilderId)
    if (!builder) return

    const request = {
      name: newName,
      shortSummary: builder.shortSummary,
      longSummary: builder.longSummary,
      audienceSummary: builder.audienceSummary,
      audienceDetails: builder.audienceDetails || '',
      geoSummary: builder.geoSummary,
      geoDetails: builder.geoDetails || '',
      seasonalitySummary: builder.seasonalitySummary,
      seasonalityDetails: builder.seasonalityDetails || '',
      campaignType: builder.campaignType as unknown as string,
      isOffAdCritter: builder.isOffAdCritter,
      offAdCritterName: builder.offAdCritterName,
      offAdCritterColor: builder.offAdCritterColor,
      intervalBudget: builder.intervalBudget,
      retargetSite: builder.retargetSite,
      isTotalBudget: builder.isTotalBudget,
      startDate: moment(builder.startDate).toISOString(),
      endDate: builder.endDate ? moment(builder.endDate).toISOString() : undefined,
      intervalUnit: (builder.intervalUnit || IntervalUnit.Month) as unknown as string,
      intervalFrequency: builder.intervalFrequency || 1,
      intervalDuration: builder.intervalDuration || 1,
      segmentTargets: builder.segmentTargets || [],
      audienceSegmentLooksUp: builder.audienceSegmentLooksUp || [],
      targetingType: builder.targetingType as unknown as string | undefined,
      regions: builder.regions || [],
      dmas: builder.dmas || [],
      cities: builder.cities || [],
      zips: builder.zips || [],
      keywords: builder.keywords || [],
      keywordDescription: builder.keywordDescription || '',
      segmentDescriptions: builder.segmentDescriptions || [],
      includeInProjections: builder.includeInProjections || false,
      includeInGeoHeatmap: builder.includeInGeoHeatmap || false
    }

    editCampaignBuilder({
      aiPlanStubId: stub.id,
      campaignBuilderId,
      aiPlanId: aiPlan.id,
      request
    })
    didFireNameUpdate.current = true
  }

  const handleDeleteSection = (sectionType: SectionType) => {
    setDeletingSectionType(sectionType)
  }

  const confirmDeleteSection = async () => {
    if (!deletingSectionType) return

    await hideSection({
      aiPlanId: aiPlan.id,
      sectionType: deletingSectionType
    })

    setDeletingSectionType(null)
  }

  const handleCancelDelete = () => {
    if (!isHidingSection) {
      setDeletingSectionType(null)
    }
  }

  const handleAddCustomSection = (position: MediaPlanCustomSectionPosition) => {
    setAddingCustomSectionPosition(position)
  }

  const handleEditCustomSection = (position: MediaPlanCustomSectionPosition, index: number) => {
    setEditingCustomSection({ position, index })
  }

  const handleDeleteCustomSection = (position: MediaPlanCustomSectionPosition, index: number) => {
    setDeletingCustomSection({ position, index })
  }

  const handleSaveCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (addingCustomSectionPosition) {
      const existingSections = mediaPlanSection?.[addingCustomSectionPosition] || []
      await addCustomSection({
        aiPlanId: aiPlan.id,
        position: addingCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingCustomSectionPosition(null)
    } else if (editingCustomSection) {
      const existingSections = mediaPlanSection?.[editingCustomSection.position] || []
      await updateCustomSection({
        aiPlanId: aiPlan.id,
        position: editingCustomSection.position,
        existingSections,
        sectionIndex: editingCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingCustomSection(null)
    }
  }

  const confirmDeleteCustomSection = async () => {
    if (!deletingCustomSection) return

    const existingSections = mediaPlanSection?.[deletingCustomSection.position] || []
    await deleteCustomSection({
      aiPlanId: aiPlan.id,
      position: deletingCustomSection.position,
      existingSections,
      sectionIndex: deletingCustomSection.index
    })

    setDeletingCustomSection(null)
  }

  const handleCancelDeleteCustomSection = () => {
    if (!isDeletingCustomSection) {
      setDeletingCustomSection(null)
    }
  }

  const handleSavePageTitle = async (newTitle: string) => {
    await updatePageTitle({
      aiPlanId: aiPlan.id,
      pageTitle: newTitle
    })
    setEditingPageTitle(false)
  }

  const handleSaveCampaignDetailsTitle = async (newTitle: string) => {
    await updateCampaignDetailsTitle({
      aiPlanId: aiPlan.id,
      campaignDetailsTitle: newTitle
    })
    setEditingCampaignDetailsTitle(false)
  }

  const getSectionName = (sectionType: SectionType | null): string => {
    if (!sectionType) return 'Section'

    const section = mediaPlanSection?.[sectionType]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return title
    }

    return 'Budget'
  }

  const getDeleteCustomSectionTitle = (): string => {
    if (!deletingCustomSection) return 'Delete Custom Section'

    const sections = mediaPlanSection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Delete ${title}`
    }

    return 'Delete Custom Section'
  }

  const getDeleteCustomSectionMessage = (): string => {
    if (!deletingCustomSection) return 'Are you sure you want to delete this custom section?'

    const sections = mediaPlanSection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Are you sure you want to delete ${title}?`
    }

    return 'Are you sure you want to delete this custom section?'
  }

  const handlePdfDownload = async () => {
    try {
      setIsGeneratingPdf(true)
      const mapImages = await captureMapImages()
      const filename = `${aiPlan.name} - Media Plan.pdf`
      await downloadPdf(<AIPlanMediaPlanPdf campaigns={campaigns} campaignMapImages={mapImages} />, filename)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return (
    <>
      <EuiFlexGroup justifyContent='spaceBetween'>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiTitle size='m'>
                <h3>{mediaPlanSection?.pageTitle || 'Media Plan'}</h3>
              </EuiTitle>
            </EuiFlexItem>
            {editMode && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content='Edit Title'>
                    <EuiButtonIcon iconType='visText' style={{ color: '#0071c2' }} onClick={() => setEditingPageTitle(true)} aria-label='Edit page title' size='s' />
                  </EuiToolTip>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup>
            {onAllStaffPdfsClick && (
              <EuiFlexItem grow={false}>
                {staffPdfDisabledReason ? (
                  <EuiToolTip content={staffPdfDisabledReason}>
                    <EuiButton size='s' color='primary' disabled>
                      All Staff PDFs
                    </EuiButton>
                  </EuiToolTip>
                ) : (
                  <EuiButton size='s' color='primary' onClick={onAllStaffPdfsClick}>
                    All Staff PDFs
                  </EuiButton>
                )}
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButton size='s' color='primary' onClick={handlePdfDownload} isLoading={isGeneratingPdf} disabled={isGeneratingPdf}>
                Client PDF
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiHorizontalRule />

      {!budgetHidden && (
        <>
          <AIPlanBudget campaigns={campaigns as unknown as MasterCampaignPlanCampaignBuilder[]} section={mediaPlanSection?.budget} onDelete={() => handleDeleteSection('budget')} showActions={editMode} />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={mediaPlanSection?.aboveCampaignTimeline as any} position={'aboveCampaignTimeline' as MediaPlanCustomSectionPosition} editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {editMode && (
        <>
          <AddCustomSectionButton position={'aboveCampaignTimeline' as MediaPlanCustomSectionPosition} onClick={handleAddCustomSection} />
          <EuiSpacer size='xl' />
        </>
      )}

      <BackgroundContentCard>
        <EuiFlexGroup justifyContent='center'>
          <EuiFlexItem style={{ textAlign: 'center' }}>
            <EuiTitle size='s'>
              <h4>Campaign Timeline</h4>
            </EuiTitle>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size='m' />
        {isStubLoading ? (
          <EuiText>
            <p>Loading campaign data...</p>
          </EuiText>
        ) : (
          <GanttChart campaigns={campaigns as unknown as MasterCampaignPlanCampaignBuilder[]} planStartDate={planStartDate} planDurationMonths={planDurationMonths} isClickable={true} isHoverable={true} presorted />
        )}
      </BackgroundContentCard>

      <EuiSpacer size='xxl' />

      <RenderCustomSections sections={mediaPlanSection?.aboveCampaignDetails as any} position={'aboveCampaignDetails' as MediaPlanCustomSectionPosition} editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {editMode && (
        <>
          <AddCustomSectionButton position={'aboveCampaignDetails' as MediaPlanCustomSectionPosition} onClick={handleAddCustomSection} />
          <EuiSpacer size='m' />
        </>
      )}

      <EuiFlexGroup alignItems='center' justifyContent='spaceBetween'>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiTitle size='s'>
                <h4>{mediaPlanSection?.campaignDetailsTitle || 'Campaign Details'}</h4>
              </EuiTitle>
            </EuiFlexItem>
            {editMode && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content='Edit Title'>
                    <EuiButtonIcon iconType='visText' style={{ color: '#0071c2' }} onClick={() => setEditingCampaignDetailsTitle(true)} aria-label='Edit campaign details title' size='s' />
                  </EuiToolTip>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup gutterSize='s'>
            {editMode && aiPlan.status !== AIPlanStatus.CopiedToDsp && (
              <EuiFlexItem grow={false}>
                <EuiButton size='s' onClick={onAddCampaignClick}>
                  <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
                    <EuiFlexItem grow={false}>Add Campaign</EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <SparklesIcon />
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiButton>
              </EuiFlexItem>
            )}
            {aiPlan.status === AIPlanStatus.Ready && (
              <EuiFlexItem grow={false}>
                <EuiButton size='s' color='primary' fill onClick={() => setIsCopyModalVisible(true)}>
                  Copy All to DSP
                </EuiButton>
              </EuiFlexItem>
            )}
            {aiPlan.status === AIPlanStatus.CopiedToDsp && aiPlan.masterCampaignId && (
              <EuiFlexItem grow={false}>
                <EuiButton size='s' color='primary' onClick={() => history.push(`/campaigns/master/${aiPlan.masterCampaignId}`)}>
                  View Master Campaign in DSP
                </EuiButton>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size='m' />

      {campaigns.length === 0 && (
        <EuiPanel>
          <EuiText textAlign='center' color='subdued'>
            <p>No campaigns yet</p>
          </EuiText>
        </EuiPanel>
      )}

      {campaigns.map((builder, index) => (
        <div key={builder.id}>
          <AIPlanCampaignCard
            builder={builder}
            planStatus={aiPlan.status}
            onEditBasicsClick={showActions ? () => setEditBasicsFlyoutCampaignId(builder.id) : undefined}
            onEditNameClick={showActions ? () => setEditCampaignNameState({ campaignBuilderId: builder.id, currentName: builder.name }) : undefined}
            onEditSectionClick={showActions ? (sectionType: CampaignSectionType) => setEditSectionFlyoutState({ campaignBuilderId: builder.id, sectionType }) : undefined}
            onRemoveCampaignClick={showActions ? () => onRemoveCampaignClick(builder) : undefined}
            onAIEditSection={showActions ? (sectionLabel: string, campaignSectionName: string) => setAiEditFlyoutState({ targetType: campaignSectionToTargetType[campaignSectionName], targetId: aiPlan.id, targetLabel: sectionLabel, campaignBuilderId: builder.id, campaignType: builder.campaignType as unknown as string }) : undefined}
            onAIEditCampaign={showActions ? () => setAiEditFlyoutState({ targetType: 'EditCampaign', targetId: aiPlan.id, targetLabel: `Edit Campaign - ${builder.name}`, campaignBuilderId: builder.id, campaignType: builder.campaignType as unknown as string }) : undefined}
            onFullEditClick={showActions && builder.isOffAdCritter ? () => setEditCampaignFlyoutOpen({ mode: EditAIPlanCampaignFlyoutMode.Edit, selectedCampaignBuilderId: builder.id }) : undefined}
            onProjectionSettingsClick={showActions && builder.isOffAdCritter ? () => setProjectionSettingsCampaignId(builder.id) : undefined}
            onCopyCampaignClick={() => onCopyCampaignToDsp(builder)}
            onViewInDspClick={onViewInDspClick}
            isRemovingCampaign={isRemovingCampaign}
            isCopyingCampaign={isCopyingCampaign}
            showActions={showActions}
          />
          {index < campaigns.length - 1 && <EuiSpacer size='l' />}
        </div>
      ))}

      {campaigns.map(campaign => {
        if (!campaign.zips || campaign.zips.length === 0) return null

        const isBillboard = campaign.campaignType === AIPlanCampaignBuilderType.Billboard
        const isHousehold = campaign.campaignType === AIPlanCampaignBuilderType.Internet || campaign.campaignType === AIPlanCampaignBuilderType.TV || (campaign.isOffAdCritter && campaign.includeInProjections && campaign.includeInGeoHeatmap)

        if (!isBillboard && !isHousehold) return null

        return (
          <div key={`campaign-map-${campaign.id}`} style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '800px', height: '600px', overflow: 'visible' }}>
            {isHousehold && stub && (
              <AIPlanTargetedHouseholdLocations
                ref={mapRef => {
                  if (mapRef) {
                    campaignMapRefs.current.set(campaign.id, mapRef)
                  } else {
                    campaignMapRefs.current.delete(campaign.id)
                  }
                }}
                aiPlan={aiPlan}
                stub={{ ...stub, campaignBuilders: [campaign] }}
                isCapturing
              />
            )}
            {isBillboard && stub && (
              <CampaignSpecificBillboardMap
                ref={mapRef => {
                  if (mapRef) {
                    campaignMapRefs.current.set(campaign.id, mapRef)
                  } else {
                    campaignMapRefs.current.delete(campaign.id)
                  }
                }}
                masterCampaignStub={{ campaignBuilders: [campaign as unknown as MasterCampaignPlanCampaignBuilder] } as any}
                campaign={campaign as unknown as MasterCampaignPlanCampaignBuilder}
                isCapturing
              />
            )}
          </div>
        )
      })}

      {(addingCustomSectionPosition || editingCustomSection) && (
        <EditCustomSectionFlyout
          section={editingCustomSection ? (mediaPlanSection?.[editingCustomSection.position]?.[editingCustomSection.index] as any) : undefined}
          defaultTitle=''
          defaultContent=''
          defaultShowTitle={true}
          flyoutTitle={addingCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSaveCustomSection}
          onClose={() => {
            setAddingCustomSectionPosition(null)
            setEditingCustomSection(null)
          }}
          isLoading={isUpdatingAnySection}
        />
      )}

      {deletingSectionType && <AcConfirmModal message='Are you sure you want to delete this section?' title={`Delete ${getSectionName(deletingSectionType)}`} onCancel={handleCancelDelete} onConfirm={confirmDeleteSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isHidingSection} confirmButtonDisabled={isHidingSection} />}

      {deletingCustomSection && <AcConfirmModal message={getDeleteCustomSectionMessage()} title={getDeleteCustomSectionTitle()} onCancel={handleCancelDeleteCustomSection} onConfirm={confirmDeleteCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingCustomSection} confirmButtonDisabled={isDeletingCustomSection} />}

      {editingPageTitle && <EditPageTitleFlyout currentTitle={mediaPlanSection?.pageTitle || 'Media Plan'} defaultTitle='Media Plan' onSave={handleSavePageTitle} onClose={() => setEditingPageTitle(false)} isLoading={isUpdatingPageTitle} />}

      {editingCampaignDetailsTitle && <EditPageTitleFlyout currentTitle={mediaPlanSection?.campaignDetailsTitle || 'Campaign Details'} defaultTitle='Campaign Details' onSave={handleSaveCampaignDetailsTitle} onClose={() => setEditingCampaignDetailsTitle(false)} isLoading={isUpdatingCampaignDetailsTitle} />}

      {aiEditFlyoutState && (
        <AIEditPromptFlyout
          targetType={aiEditFlyoutState.targetType}
          targetLabel={aiEditFlyoutState.targetLabel}
          promptLabel={aiEditFlyoutState.promptLabel}
          promptPlaceholder={aiEditFlyoutState.promptPlaceholder}
          showConstraints={aiEditFlyoutState.showConstraints}
          hideChannelType={aiEditFlyoutState.targetType === 'AddCampaign' && !!aiEditFlyoutState.constraintCampaignType}
          campaignType={aiEditFlyoutState.campaignType}
          initialConstraints={
            aiEditFlyoutState.targetType === 'EditCampaign' && aiEditFlyoutState.campaignBuilderId
              ? (() => {
                  const builder = campaigns.find(c => c.id === aiEditFlyoutState.campaignBuilderId)
                  if (!builder) return undefined
                  return {
                    campaignType: builder.campaignType as unknown as string,
                    startDate: moment(builder.startDate).format('YYYY-MM-DD'),
                    duration: String(builder.intervalDuration ?? 1),
                    budget: String(builder.intervalBudget ?? 0)
                  }
                })()
              : undefined
          }
          onSubmit={(prompt: string, constraints?: { campaignType?: string; startDate?: string; duration?: string; budget?: string }, deepThink?: boolean) => {
            const updatedState = {
              ...aiEditFlyoutState,
              constraintCampaignType: constraints?.campaignType || aiEditFlyoutState.constraintCampaignType,
              constraintStartDate: constraints?.startDate,
              constraintDuration: constraints?.duration,
              constraintBudget: constraints?.budget
            }
            submitAIEdit(prompt, updatedState, deepThink)
          }}
          onClose={() => setAiEditFlyoutState(null)}
          isSubmitting={isAIEditSubmitting}
        />
      )}

      {isAddCampaignFlyoutOpen && <AddAIPlanCampaignFlyout aiPlanId={aiPlan.id} onClose={() => setIsAddCampaignFlyoutOpen(false)} onSubmitAIEdit={submitAIEdit} isAIEditSubmitting={isAIEditSubmitting} />}

      {editCampaignFlyoutOpen && <EditAIPlanCampaignFlyout editMode={editCampaignFlyoutOpen.mode} selectedCampaignBuilderId={editCampaignFlyoutOpen.selectedCampaignBuilderId} offAdCritterName={editCampaignFlyoutOpen.offAdCritterName} offAdCritterColor={editCampaignFlyoutOpen.offAdCritterColor} onClose={() => setEditCampaignFlyoutOpen(null)} />}

      {projectionSettingsCampaignId && <EditAIPlanCampaignProjectionsFlyout campaignBuilderId={projectionSettingsCampaignId} onClose={() => setProjectionSettingsCampaignId(null)} />}

      {editBasicsFlyoutCampaignId && <EditAIPlanCampaignBasicsFlyout campaignBuilderId={editBasicsFlyoutCampaignId} onClose={() => setEditBasicsFlyoutCampaignId(null)} />}

      {editSectionFlyoutState &&
        (() => {
          const editBuilder = stub?.campaignBuilders?.find(b => b.id === editSectionFlyoutState.campaignBuilderId)
          if (editSectionFlyoutState.sectionType === 'keywords') {
            return <EditAIPlanCampaignKeywordsFlyout campaignBuilderId={editSectionFlyoutState.campaignBuilderId} onClose={() => setEditSectionFlyoutState(null)} />
          }
          if (editSectionFlyoutState.sectionType === 'audience' && editBuilder?.isOffAdCritter) {
            return <EditAIPlanCampaignAudienceFlyout campaignBuilderId={editSectionFlyoutState.campaignBuilderId} onClose={() => setEditSectionFlyoutState(null)} />
          }
          return <EditAIPlanCampaignSectionFlyout campaignBuilderId={editSectionFlyoutState.campaignBuilderId} sectionType={editSectionFlyoutState.sectionType} onClose={() => setEditSectionFlyoutState(null)} />
        })()}

      {editCampaignNameState && <EditPageTitleFlyout currentTitle={editCampaignNameState.currentName} defaultTitle='Campaign Name' onSave={newName => handleSaveCampaignName(editCampaignNameState.campaignBuilderId, newName)} onClose={() => setEditCampaignNameState(null)} isLoading={isEditingCampaignName} fullWidth />}

      {isCopyModalVisible && (
        <AgencyV2Modal isLoading={isCopying} title='Copy to DSP' onClose={() => setIsCopyModalVisible(false)} confirmButtonText='Copy' onConfirmButtonClick={onCopyToDsp} showCancelButton>
          <EuiText>
            <p>This will copy all campaigns and associated documents to AdCritter DSP where you will have full editing control.</p>
          </EuiText>
          <EuiSpacer size='m' />
          <EuiText>
            <p>Are you ready to copy this plan to the DSP?</p>
          </EuiText>
        </AgencyV2Modal>
      )}
    </>
  )
})

export default AIPlanMediaPlan
